import { downloadCsv } from "./csvExport";
import { downloadHtmlAsPdf } from "./htmlToPdf";

export const getAvailableTypes = (types = []) =>
  (types || []).filter((type) => type.isAvailable !== false);

export const sortTypesBySalesDesc = (types = []) =>
  [...types].sort((a, b) => (b.sales || 0) - (a.sales || 0));

export const getSalesRange = (types = []) => {
  if (!types.length) return { min: 0, max: 0 };
  const sales = types.map((type) => Number(type.sales) || 0);
  return { min: Math.min(...sales), max: Math.max(...sales) };
};

/** Blue (low) → yellow → red (high) heatmap colors. */
export const getSalesHeatStyle = (sales, min, max) => {
  const value = Number(sales) || 0;
  if (max <= min) {
    const hot = value > 0;
    return {
      backgroundColor: hot ? "hsl(8, 82%, 52%)" : "hsl(220, 14%, 92%)",
      color: hot ? "#fff" : "#374151",
    };
  }
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const hue = 218 * (1 - t);
  const sat = 72 + t * 18;
  const light = 90 - t * 42;
  return {
    backgroundColor: `hsl(${hue}, ${sat}%, ${light}%)`,
    color: t > 0.58 ? "#fff" : "#111827",
  };
};

const todayStamp = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const safeFilePart = (name) =>
  String(name || "brand")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .trim() || "brand";

const typeId = (type) => type.id ?? type.typeId;
const typeSales = (type) => Number(type.sales) || 0;
const typeStock = (type) => type.stockQuantity ?? 0;

export const exportBrandAvailableTypesCsv = (brandName, types = []) => {
  const available = sortTypesBySalesDesc(getAvailableTypes(types));
  if (!available.length) {
    throw new Error("لا توجد أنواع متاحة لهذا البراند");
  }
  const headers = ["الترتيب", "رقم النوع", "اسم النوع", "المبيعات", "المخزن"];
  const rows = available.map((type, index) => [
    index + 1,
    typeId(type),
    type.name,
    typeSales(type),
    typeStock(type),
  ]);
  downloadCsv(
    headers,
    rows,
    `انواع-متاحة-${safeFilePart(brandName)}-${todayStamp()}.csv`
  );
  return available.length;
};

export const exportAllBrandsAvailableTypesCsv = (groupedBrands = []) => {
  const headers = [
    "البراند",
    "الترتيب داخل البراند",
    "رقم النوع",
    "اسم النوع",
    "المبيعات",
    "المخزن",
  ];
  const rows = [];
  groupedBrands.forEach((brand) => {
    brand.types.forEach((type, index) => {
      rows.push([
        brand.name,
        index + 1,
        typeId(type),
        type.name,
        typeSales(type),
        typeStock(type),
      ]);
    });
  });
  if (!rows.length) {
    throw new Error("لا توجد أنواع متاحة");
  }
  downloadCsv(headers, rows, `انواع-متاحة-كل-البراندات-${todayStamp()}.csv`);
  return rows.length;
};

export const groupAvailableTypesByBrand = (brands = [], types = []) => {
  const brandNameById = new Map(
    (brands || []).map((brand) => [brand.brandId ?? brand.id, brand.name])
  );
  const available = sortTypesBySalesDesc(getAvailableTypes(types)).map((type) => {
    const brandId = type.brandId;
    return {
      id: typeId(type),
      name: type.name,
      sales: typeSales(type),
      stockQuantity: typeStock(type),
      brandId,
      brandName: brandNameById.get(brandId) || `براند #${brandId}`,
      isAvailable: true,
    };
  });

  const byBrand = new Map();
  available.forEach((type) => {
    const key = type.brandId;
    if (!byBrand.has(key)) {
      byBrand.set(key, {
        id: key,
        name: type.brandName,
        types: [],
      });
    }
    byBrand.get(key).types.push(type);
  });

  return [...byBrand.values()].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), "ar", { sensitivity: "base" })
  );
};

const heatCellHtml = (type, min, max) => {
  const style = getSalesHeatStyle(typeSales(type), min, max);
  return `<div class="heat-cell" style="background:${style.backgroundColor};color:${style.color}">
    <div class="heat-name">${escapeHtml(type.name)}</div>
    <div class="heat-sales">${typeSales(type)}</div>
  </div>`;
};

const brandSectionHtml = (title, types) => {
  const sorted = sortTypesBySalesDesc(types);
  const { min, max } = getSalesRange(sorted);
  const totalSales = sorted.reduce((sum, type) => sum + typeSales(type), 0);
  const top = sorted[0];
  const low = sorted[sorted.length - 1];
  const cells = sorted.map((type) => heatCellHtml(type, min, max)).join("");
  const rows = sorted
    .map(
      (type, index) => `<tr>
        <td>${index + 1}</td>
        <td>${typeId(type)}</td>
        <td>${escapeHtml(type.name)}</td>
        <td>${typeSales(type)}</td>
        <td>${typeStock(type)}</td>
      </tr>`
    )
    .join("");

  return `
    <section class="brand-block pdf-block">
      <h2>${escapeHtml(title)}</h2>
      <div class="stats">
        <span>الأنواع المتاحة: <b>${sorted.length}</b></span>
        <span>إجمالي المبيعات: <b>${totalSales}</b></span>
        ${top ? `<span>الأعلى: <b>${escapeHtml(top.name)}</b> (${typeSales(top)})</span>` : ""}
        ${low ? `<span>الأقل: <b>${escapeHtml(low.name)}</b> (${typeSales(low)})</span>` : ""}
      </div>
      <div class="legend">
        <span>أقل مبيعات</span>
        <div class="legend-bar"></div>
        <span>أكثر مبيعات</span>
      </div>
      <div class="heat-grid">${cells}</div>
      <table>
        <thead>
          <tr>
            <th>الترتيب</th>
            <th>ID</th>
            <th>اسم النوع</th>
            <th>المبيعات</th>
            <th>المخزن</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </section>
  `;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const reportCss = `
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body {
    font-family: "CairoReport", "Cairo", "Segoe UI", Tahoma, sans-serif;
    direction: rtl;
    color: #111827;
    margin: 0;
    padding: 16px;
    background: #fff;
  }
  h1 { margin: 0 0 6px; font-size: 22px; }
  .meta { color: #6b7280; margin-bottom: 18px; font-size: 13px; }
  h2 { margin: 0 0 10px; font-size: 18px; color: #6d28d9; }
  .brand-block { page-break-inside: avoid; margin-bottom: 28px; }
  .stats { display: flex; flex-wrap: wrap; gap: 12px 18px; margin-bottom: 10px; font-size: 13px; }
  .legend { display: flex; align-items: center; gap: 8px; font-size: 12px; margin-bottom: 10px; color: #4b5563; }
  .legend-bar {
    width: 160px; height: 10px; border-radius: 999px;
    background: linear-gradient(to left, hsl(218,72%,90%), hsl(55,85%,62%), hsl(8,82%,52%));
  }
  .heat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .heat-cell {
    border-radius: 12px;
    padding: 10px 8px;
    text-align: center;
    min-height: 72px;
  }
  .heat-name { font-weight: 700; font-size: 13px; line-height: 1.3; }
  .heat-sales { font-size: 18px; font-weight: 800; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: center; }
  th { background: #f5f3ff; }
  td:nth-child(3), th:nth-child(3) { text-align: right; }
`;

const buildReportHtml = (title, bodyHtml) => {
  const generatedAt = new Date().toLocaleString("ar-EG");
  const headerHtml = `
    <h1>${escapeHtml(title)}</h1>
    <div class="meta">الأنواع المتاحة فقط · ${escapeHtml(generatedAt)}</div>
  `;
  const marker = 'class="brand-block pdf-block">';
  const idx = bodyHtml.indexOf(marker);
  const content =
    idx === -1
      ? `${headerHtml}${bodyHtml}`
      : `${bodyHtml.slice(0, idx + marker.length)}${headerHtml}${bodyHtml.slice(idx + marker.length)}`;

  return `<style>
@font-face {
  font-family: "CairoReport";
  src: url("${window.location.origin}/fonts/Cairo.ttf") format("truetype");
  font-weight: 400 700;
}
${reportCss}
.pdf-root { width: 794px; background: #fff; padding: 16px; }
</style>
<div class="pdf-root">${content}</div>`;
};

export const downloadBrandAvailableTypesPdf = async (brandName, types = []) => {
  const available = sortTypesBySalesDesc(getAvailableTypes(types));
  if (!available.length) {
    throw new Error("لا توجد أنواع متاحة لهذا البراند");
  }
  const title = `تقرير الأنواع المتاحة · ${brandName || "براند"}`;
  await downloadHtmlAsPdf({
    html: buildReportHtml(title, brandSectionHtml(brandName || "براند", available)),
    fileName: `تقرير-انواع-متاحة-${safeFilePart(brandName)}-${todayStamp()}.pdf`,
  });
  return available.length;
};

export const downloadAllBrandsAvailableTypesPdf = async (groupedBrands = []) => {
  if (!groupedBrands.length) {
    throw new Error("لا توجد أنواع متاحة");
  }
  const body = groupedBrands
    .map((brand) => brandSectionHtml(brand.name, brand.types))
    .join("");
  await downloadHtmlAsPdf({
    html: buildReportHtml("تقرير الأنواع المتاحة لكل البراندات", body),
    fileName: `تقرير-انواع-متاحة-كل-البراندات-${todayStamp()}.pdf`,
  });
  return groupedBrands.reduce((sum, brand) => sum + (brand.types?.length || 0), 0);
};
